// SEO helper: builds a TanStack Router `head()` return object with
// canonical, Open Graph, Twitter and JSON-LD from a single input.
//
// Canonical domain is ALWAYS https://bizaclia.com — never the preview host.

export const SEO_ORIGIN = "https://bizaclia.com";
export const SEO_DEFAULT_OG_IMAGE = `${SEO_ORIGIN}/og-default.jpg`;
export const SEO_DEFAULT_OG_ALT = "Bizaclia · Portal inmobiliario para negocios";

export type SeoInput = {
  title: string;
  description: string;
  path?: string;
  canonicalUrl?: string;
  image?: string;
  imageAlt?: string;
  type?: "website" | "article" | "product" | "profile";
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
};

export function cleanPath(input: string): string {
  if (!input) return "/";
  let p = input;
  try {
    if (/^https?:\/\//i.test(input)) p = new URL(input).pathname;
  } catch {
    // ignore
  }
  p = p.split("?")[0].split("#")[0];
  p = p.toLowerCase();
  if (!p.startsWith("/")) p = "/" + p;
  p = p.replace(/\/{2,}/g, "/");
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p;
}

export function canonicalFor(path: string): string {
  return `${SEO_ORIGIN}${cleanPath(path)}`;
}

function safeJsonLd(data: unknown): string {
  return JSON.stringify(data).replace(/<\//g, "<\\/");
}

export function buildSeo(input: SeoInput) {
  const url = input.canonicalUrl ?? canonicalFor(input.path ?? "/");
  const image = input.image ?? SEO_DEFAULT_OG_IMAGE;
  const imageAlt = input.imageAlt ?? SEO_DEFAULT_OG_ALT;
  const type = input.type ?? "website";
  const robots = input.noindex ? "noindex, follow" : "index, follow";

  const meta: Array<Record<string, string>> = [
    { title: input.title },
    { name: "description", content: input.description },
    { name: "robots", content: robots },
    { property: "og:title", content: input.title },
    { property: "og:description", content: input.description },
    { property: "og:url", content: url },
    { property: "og:type", content: type },
    { property: "og:site_name", content: "Bizaclia" },
    { property: "og:locale", content: "es_ES" },
    { property: "og:image", content: image },
    { property: "og:image:alt", content: imageAlt },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: "@bizaclia" },
    { name: "twitter:title", content: input.title },
    { name: "twitter:description", content: input.description },
    { name: "twitter:image", content: image },
  ];

  const links = [{ rel: "canonical", href: url }];

  const jsonLdArr = input.jsonLd
    ? Array.isArray(input.jsonLd)
      ? input.jsonLd
      : [input.jsonLd]
    : [];

  const scripts = jsonLdArr.map((obj) => ({
    type: "application/ld+json",
    children: safeJsonLd(obj),
  }));

  return { meta, links, scripts };
}

// ─────────────────────────────────────────────────────────────
// Portal facet helpers
// ─────────────────────────────────────────────────────────────

export const PORTAL_OPERACIONES = new Set([
  "traspaso",
  "alquiler",
  "venta",
  "rentabilidad",
  "todas",
]);

/** Display noun used inside descriptions ("en traspaso", "en venta"…). */
export function operacionLabel(slug: string): string {
  switch (slug) {
    case "traspaso":
      return "traspaso";
    case "alquiler":
      return "alquiler";
    case "venta":
      return "venta";
    case "rentabilidad":
      return "venta en rentabilidad";
    default:
      return "";
  }
}

/** Capitalized display form used in titles/H1 ("Traspaso", "Alquiler"…). */
export function operacionDisplay(slug: string): string | null {
  switch (slug) {
    case "traspaso":
      return "Traspaso";
    case "alquiler":
      return "Alquiler";
    case "venta":
      return "Venta";
    case "rentabilidad":
      return "Venta en rentabilidad";
    default:
      return null;
  }
}

/**
 * Plural noun for a given `usos_local` slug or nombre, used inside
 * titles/descriptions ("restaurantes", "locales comerciales", …).
 * Falls back to a lowercase pluralization of the passed name.
 */
export function tipoPlural(input: string | null | undefined): string {
  if (!input) return "locales comerciales";
  const key = input.toLowerCase().trim();

  // Explicit map for the common categories requested in the SEO spec.
  const map: Record<string, string> = {
    // Slug forms
    restaurante: "restaurantes",
    comercial: "locales comerciales",
    oficina: "oficinas",
    nave: "naves",
    parking: "parkings y garajes",
    "parking-garaje": "parkings y garajes",
    garaje: "parkings y garajes",
    trastero: "trasteros",
    "centro-de-trasteros": "centros de trasteros",
    otros: "otros locales",
    bar: "bares",
    "bar-musical-pub": "bares musicales y pubs",
    cafeteria: "cafeterías",
    pizzeria: "pizzerías",
    fruteria: "fruterías",
    "local-de-comida-rapida-fast-food": "locales de comida rápida",
    "local-de-comida-para-llevar-take-away": "locales de comida para llevar",
    "panaderia-pasteleria": "panaderías y pastelerías",
    heladeria: "heladerías",
    "cocina-de-produccion-dark-kitchen": "cocinas de producción (dark kitchen)",
    discoteca: "discotecas",
    "sala-de-conciertos": "salas de conciertos",
    hotel: "hoteles",
    masia: "masías",
    "castillo-palacio": "castillos y palacios",
    casino: "casinos",
    gimnasio: "gimnasios",
    "clinica-dental": "clínicas dentales",
    "clinica-veterinaria": "clínicas veterinarias",
    "centro-de-estetica": "centros de estética",
    "centro-educativo": "centros educativos",
    coworking: "espacios de coworking",
    spa: "spas",
    "local-de-unas": "locales de uñas",
    "local-de-moda": "locales de moda",
    estancos: "estancos",
    "tintoreria-lavanderia": "tintorerías y lavanderías",
    floristeria: "floristerías",
    "taller-automocion": "talleres de automoción",
    "taller-industrial": "talleres industriales",
    peluqueria: "peluquerías",
    barberia: "barberías",
    chatarreria: "chatarrerías",
    almacen: "almacenes",
    fabrica: "fábricas",
    // Common `nombre` forms (already accented, capitalized)
    "restaurante ": "restaurantes",
  };
  if (map[key]) return map[key];

  // Fallback pluralization from the nombre.
  const lower = key.replace(/^\s+|\s+$/g, "");
  if (lower.endsWith("s")) return lower;
  if (/[aeiouáéíóú]$/.test(lower)) return `${lower}s`;
  return `${lower}es`;
}

/** Capitalize a slug for display ("sant-cugat" → "Sant Cugat"). */
export function humanizeSlug(slug: string): string {
  if (!slug || slug === "todas") return "";
  return slug
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

/**
 * Return true when the request has any FILTER query param — i.e. a param that
 * refines the base facet path (uuid multi-selects, price/superficie ranges,
 * pagination…). Presence of any of these means: canonical still points to the
 * clean facet path, but the current URL is a filtered variant → noindex.
 * A few purely diagnostic keys (like `demo`) are ignored.
 */
export function hasFilterQuery(
  search: Record<string, unknown> | null | undefined,
): boolean {
  if (!search) return false;
  const ignore = new Set(["demo"]);
  for (const [k, v] of Object.entries(search)) {
    if (ignore.has(k)) continue;
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v) && v.length === 0) continue;
    return true;
  }
  return false;
}

/**
 * Build the SEO head for a portal facet page given the resolved labels
 * (already looked up from the database) plus the URL segments.
 */
export type PortalSeoLabels = {
  op?: string; // slug: "traspaso" | "alquiler" | "venta" | "rentabilidad" | ""
  tipoSlug?: string; // slug in URL
  tipoNombre?: string | null; // nombre from BD (uso_local)
  provincia?: string | null; // nombre from BD
  ciudad?: string | null;
  distrito?: string | null; // (schema-wise, barrio)
};

export function portalTitleFor(
  labels: PortalSeoLabels,
  segsLength: number,
): { title: string; h1: string } {
  const brand = " | Bizaclia";
  const opDisplay = operacionDisplay(labels.op ?? "");
  const opLower = operacionLabel(labels.op ?? "");
  const plural = tipoPlural(labels.tipoNombre ?? labels.tipoSlug);

  // Depth 0: /portal
  if (segsLength === 0) {
    const h1 = "Locales, oficinas y naves en traspaso, venta y alquiler";
    return { title: h1 + brand, h1 };
  }

  // No operation (or "todas")
  if (!opDisplay) {
    const loc = locationTail(labels);
    const subject = labels.tipoNombre || labels.tipoSlug
      ? plural.charAt(0).toUpperCase() + plural.slice(1)
      : "Locales, oficinas y naves";
    const h1 = `${subject} en traspaso, venta y alquiler${loc ? " en " + loc : ""}`;
    return { title: h1 + brand, h1 };
  }

  // /portal/{op}
  if (segsLength === 1) {
    const h1 = `${opDisplay} de locales y negocios en España`;
    return { title: h1 + brand, h1 };
  }

  // /portal/{op}/{tipo}[...]
  const loc = locationTail(labels);
  const location = loc || "España";
  const opAction = `${opDisplay} de ${plural}`;
  const h1 = `${opAction} en ${location}`;
  return { title: h1 + brand, h1 };
}

function locationTail(labels: PortalSeoLabels): string {
  if (labels.distrito && labels.ciudad)
    return `${labels.distrito}, ${labels.ciudad}`;
  if (labels.ciudad) return labels.ciudad;
  if (labels.provincia) return labels.provincia;
  return "";
}

export function portalDescriptionFor(
  labels: PortalSeoLabels,
  segsLength: number,
  count?: number | null,
): string {
  const opLower = operacionLabel(labels.op ?? "");
  const plural = tipoPlural(labels.tipoNombre ?? labels.tipoSlug);
  const loc = locationTail(labels);

  if (segsLength === 0) {
    return "Encuentra locales, oficinas y naves en traspaso, venta o alquiler en toda España. Filtra por precio, superficie, ubicación y tipo de negocio en Bizaclia.";
  }

  if (!opLower && segsLength === 1) {
    return `Descubre ${plural} en traspaso, venta o alquiler${loc ? " en " + loc : ""} en Bizaclia, el portal inmobiliario para negocios.`;
  }

  const subject = plural;
  const opPart = opLower ? ` en ${opLower}` : "";
  const locPart = loc ? ` en ${loc}` : " en España";
  const numPart =
    typeof count === "number" && count > 0 ? ` Compara ${count} anuncios,` : "";
  return `Descubre ${subject}${opPart}${locPart}:${numPart} precios, superficie, licencias y contacto directo. Anuncios verificados y asesoramiento de Bizaclia.`.replace(
    /\s+/g,
    " ",
  );
}

/** Build the Schema.org BreadcrumbList graph for the facet chain. */
export function portalBreadcrumbJsonLd(
  labels: PortalSeoLabels,
  path: string,
): Record<string, unknown> {
  const items: Array<{ name: string; item: string }> = [
    { name: "Inicio", item: `${SEO_ORIGIN}/` },
    { name: "Portal", item: `${SEO_ORIGIN}/portal` },
  ];
  const segs = cleanPath(path).replace(/^\/portal\/?/, "").split("/").filter(Boolean);
  let acc = "/portal";
  const labelSeq: (string | null | undefined)[] = [
    operacionDisplay(labels.op ?? ""),
    labels.tipoNombre,
    labels.provincia,
    labels.ciudad,
    labels.distrito,
  ];
  for (let i = 0; i < segs.length; i++) {
    acc += `/${segs[i]}`;
    const name = labelSeq[i] || humanizeSlug(segs[i]);
    if (name) items.push({ name, item: `${SEO_ORIGIN}${acc}` });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.item,
    })),
  };
}

/** Build a Schema.org ItemList from the first N loaded listings. */
export function portalItemListJsonLd(
  items: Array<{ id: string; title: string }>,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SEO_ORIGIN}/local/${it.id}`,
      name: it.title,
    })),
  };
}
